import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import InstructorLayout from "@/components/InstructorLayout";
import FileUploader, { FileTile } from "@/components/FileUploader";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Users, Loader2, Award, MessageCircle } from "lucide-react";

export default function InstructorTareaDetalle() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/instructor/assignments/${id}/deliveries`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  if (loading || !data) {
    return (
      <InstructorLayout>
        <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      </InstructorLayout>
    );
  }

  const a = data.assignment;
  return (
    <InstructorLayout>
      <Link to="/app/instructor/tareas" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-3">
        <ArrowLeft className="w-3 h-3" /> Volver a tareas
      </Link>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Tarea</p>
        <h1 className="mt-1 text-2xl font-bold text-white font-display">{a.title}</h1>
        {a.due_at && (
          <p className="mt-2 text-xs text-gray-400 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Entrega: {new Date(a.due_at).toLocaleString("es-MX")} · Puntaje máx: {a.max_score}
          </p>
        )}
        {a.description && <p className="mt-3 text-sm text-gray-300 whitespace-pre-wrap">{a.description}</p>}
        {a.attachments?.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-widest text-gray-500">Material de referencia</p>
            <div className="mt-2 grid sm:grid-cols-2 gap-2">
              {a.attachments.map((f) => <FileTile key={f.id || f.file_id} file={f} />)}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <div>
          <h2 className="text-sm font-bold text-white mb-2 inline-flex items-center gap-2">
            <Users className="w-4 h-4 text-[#F26A21]" /> Entregas ({data.items.length})
          </h2>
          {data.items.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-sm text-gray-400">
              Aún no hay entregas.
            </div>
          ) : (
            <div className="space-y-2">
              {data.items.map((d) => (
                <button key={d.id} onClick={() => setSelected(d)}
                        data-testid={`entrega-row-${d.id}`}
                        className={`w-full text-left rounded-2xl border ${selected?.id === d.id ? "border-[#F26A21] bg-white/[0.06]" : "border-white/10 bg-white/5"} p-3 hover:bg-white/[0.08]`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-white">{d.user_name || d.user_id.slice(0, 8)}</p>
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${
                      d.status === "graded" ? "bg-emerald-500/20 text-emerald-300" :
                      d.status === "returned_for_resubmission" ? "bg-amber-500/20 text-amber-300" :
                      "bg-white/10 text-gray-300"
                    }`}>{d.status === "graded" ? "Calificada" : d.status === "returned_for_resubmission" ? "Reenvío" : "Enviada"}</span>
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">{new Date(d.submitted_at).toLocaleString("es-MX")} · {d.attachments?.length || 0} archivo(s)</p>
                  {d.score !== null && d.score !== undefined && (
                    <p className="mt-1 text-xs text-emerald-300 font-semibold inline-flex items-center gap-1">
                      <Award className="w-3 h-3" /> {d.score}/{a.max_score}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {selected ? (
            <GradePanel key={selected.id} delivery={selected} assignment={a} onGraded={(fresh) => {
              setSelected(fresh);
              load();
            }} />
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-sm text-gray-400">
              Selecciona una entrega para calificar.
            </div>
          )}
        </div>
      </div>
    </InstructorLayout>
  );
}

function GradePanel({ delivery, assignment, onGraded }) {
  const [score, setScore] = useState(delivery.score ?? "");
  const [feedback, setFeedback] = useState(delivery.feedback || "");
  const [status, setStatus] = useState(delivery.status === "returned_for_resubmission" ? "returned_for_resubmission" : "graded");
  const [feedbackFiles, setFeedbackFiles] = useState(delivery.feedback_files || []);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post(`/instructor/deliveries/${delivery.id}/grade`, {
        score: score === "" ? null : Number(score),
        feedback, status,
        feedback_files: feedbackFiles,
      });
      toast.success("Calificación guardada.");
      onGraded(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="grade-panel">
      <p className="text-xs uppercase tracking-widest text-gray-500">Entrega de</p>
      <h3 className="text-lg font-bold text-white">{delivery.user_name || delivery.user_id.slice(0, 8)}</h3>
      {delivery.comments && <p className="mt-2 text-sm text-gray-300 whitespace-pre-wrap">{delivery.comments}</p>}
      <div className="mt-4">
        <p className="text-[11px] uppercase tracking-widest text-gray-500">Archivos entregados</p>
        <div className="mt-2 grid gap-2">
          {(delivery.attachments || []).map((f) => <FileTile key={f.id || f.file_id} file={f} />)}
          {delivery.attachments?.length === 0 && <p className="text-xs text-gray-500">Sin archivos.</p>}
        </div>
      </div>
      <hr className="my-4 border-white/10" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-300">Puntaje (0 – {assignment.max_score})</label>
          <input type="number" min="0" max={assignment.max_score} value={score}
                 onChange={(e) => setScore(e.target.value)}
                 data-testid="grade-score-input"
                 className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-300">Estado</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  data-testid="grade-status-select"
                  className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
            <option value="graded">Calificar y cerrar</option>
            <option value="returned_for_resubmission">Devolver para reenvío</option>
          </select>
        </div>
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-gray-300 inline-flex items-center gap-1">
          <MessageCircle className="w-3 h-3" /> Retroalimentación
        </label>
        <textarea rows={4} value={feedback} onChange={(e) => setFeedback(e.target.value)}
                  data-testid="grade-feedback-input"
                  className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                  placeholder="Comentarios, correcciones sugeridas…" />
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-gray-300">Archivos de retroalimentación (opcional)</label>
        <div className="mt-1 space-y-2">
          {feedbackFiles.map((f) => (
            <FileTile key={f.id || f.file_id} file={f} onRemove={(x) => setFeedbackFiles(p => p.filter(y => (y.id || y.file_id) !== (x.id || x.file_id)))} />
          ))}
          <FileUploader kind="assignment_feedback" resourceType="delivery" resourceId={delivery.id} compact
                        onUploaded={(f) => setFeedbackFiles(p => [...p, f])}
                        buttonLabel="Adjuntar" hint={null} />
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <button type="submit" disabled={saving} data-testid="grade-save-btn"
                className="px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar calificación
        </button>
      </div>
    </form>
  );
}
